"use client";

import { m } from "motion/react";
import Link from "next/link";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import BlueprintBackground from "@/components/BlueprintBackground";
import { useTranslation } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/translations";
import type { LucideIcon } from "lucide-react";

export interface ServiceBenefit {
  icon: LucideIcon;
  titleKey: TranslationKey;
  descKey: TranslationKey;
}

export interface ServicePageProps {
  labelKey: TranslationKey;
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  benefits: ServiceBenefit[];
  usecaseKeys: TranslationKey[];
  stack: string[];
  accentColor?: string;
  /*
    A scoping line under the hero description, small and muted. Absent by
    default, so the pages that pass nothing render exactly as before.

    It exists because a page may need to state its own limit where a reader of
    the content meets it rather than in a footer — the consulting page uses it
    to say that its regulation section is not legal advice. See
    openspec/specs/eu-regulation-expertise — "The material states its own limit".
  */
  noticeKey?: TranslationKey;
}

export default function ServicePage({
  labelKey,
  titleKey,
  descriptionKey,
  benefits,
  usecaseKeys,
  stack,
  noticeKey,
}: ServicePageProps) {
  const { t } = useTranslation();

  return (
    <>
      <BlueprintBackground />
      <main className="min-h-screen pt-32 pb-20 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Back link */}
          <m.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-12"
          >
            <Link
              href="/#solutions"
              className="inline-flex items-center gap-2 text-[10px] font-mono text-text-faint uppercase tracking-widest hover:text-mint-text transition-colors"
            >
              <ArrowLeft size={12} />
              {t("service.back")}
            </Link>
          </m.div>

          {/* Hero */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-4">
              {t(labelKey)}
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-heading font-black text-text-primary leading-[0.9] tracking-tighter uppercase mb-8">
              {t(titleKey)}
            </h1>
            <p className="text-lg sm:text-xl text-text-muted max-w-8xl leading-relaxed">
              {t(descriptionKey)}
            </p>
            {noticeKey && (
              <p className="mt-6 max-w-3xl text-sm text-text-faint leading-relaxed">
                {t(noticeKey)}
              </p>
            )}
          </m.div>

          {/* Benefits grid */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, i) => {
                const Icon = benefit.icon;
                return (
                  <m.div
                    key={benefit.titleKey}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-bg-card border border-border-faint hover:border-mint-text/30 transition-all p-8 group"
                  >
                    <Icon
                      size={28}
                      className="text-mint-text mb-4"
                      strokeWidth={1.5}
                    />
                    <h3 className="text-lg font-bold text-text-primary uppercase tracking-wide mb-3 group-hover:text-mint-text transition-colors">
                      {t(benefit.titleKey)}
                    </h3>
                    <p className="text-sm text-text-faint leading-relaxed">
                      {t(benefit.descKey)}
                    </p>
                  </m.div>
                );
              })}
            </div>
          </m.div>

          {/* Use Cases + Stack */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            {/* Use Cases */}
            <m.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-6">
                Use Cases
              </div>
              <ul className="space-y-4">
                {usecaseKeys.map((key, i) => (
                  <m.li
                    key={key}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3 text-text-muted text-sm"
                  >
                    <CheckCircle2
                      size={16}
                      className="text-mint-text mt-0.5 shrink-0"
                      strokeWidth={1.5}
                    />
                    {t(key)}
                  </m.li>
                ))}
              </ul>
            </m.div>

            {/* Tech Stack */}
            <m.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-6">
                {t("projects.techstack")}
              </div>
              <div className="flex flex-wrap gap-2">
                {stack.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 border border-border-faint bg-bg-card text-[11px] font-mono text-text-faint uppercase tracking-widest hover:border-mint-text/40 hover:text-mint-text transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </m.div>
          </div>

          {/* CTA */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-mint p-10 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8"
          >
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-black uppercase tracking-tighter mb-2">
                {t("service.cta.title")}
              </h2>
              <p className="text-black/70 font-bold">{t("service.cta.desc")}</p>
            </div>
            <Link
              href="/#contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-black tracking-widest text-xs uppercase hover:bg-black/80 transition-colors shrink-0"
            >
              {t("service.cta.button")}
              <ArrowRight size={16} />
            </Link>
          </m.div>
        </div>
      </main>
    </>
  );
}
